import { AgGridReact } from 'ag-grid-react';
import { useCallback, useRef, useMemo, useEffect } from 'react';
import { CustomGridHeader, CustomLoadingOverlay, CustomNoRowsOverlay } from './components';
import PanelLabel from './components/PanelLabel';
import { useFilterState } from './hooks/useFilterState';
import { useColumnState } from './hooks/useColumnState';
import './styles/agGridCustomStyles.scss';
import themes from './styles/agGridThemes';
import { withTheme } from '@material-ui/core';
import { withUserProfile } from '../../userProfile/userProfileContext';
import { compose } from 'recompose';
import GRID_CONSTANTS from './GridConstants';
import appConstants from '../../appConstants';
// Ag-Grid Migration DOC: https://confluence.optym.net/display/CONROUTEMAX/Ag-Grid+Migration+Guide

const DataGridNew = ({
  onGridReady,
  config,
  rowData,
  panelLabel,
  layoutContainer,
  showErrorMessage,
  theme,
  userData,
  onModelUpdated,
  onRowDataUpdated,
  onFilterChanged,
  hasActiveColumnAndQuickFilter,
  ...other
}) => {
  const gridApiRef = useRef(null);
  const { filteredData, onGridFilterChanged } = useFilterState({gridApiRef, panelLabel, onFilterChanged});
  const { onColumnMoved, onDragStopped } = useColumnState(gridApiRef, userData);

  const isRowPresent = useCallback  (() => {
    return gridApiRef.current.getDisplayedRowCount?.() > 0;
  }, []);

  const showGridOverlay = useCallback((message) => {
    const api = gridApiRef.current;
    if (!api) return;
    const { LOADING, SUCCESS, ERROR } = appConstants.GRID_STATUS;
    switch (message) {
      case ERROR:
        api.hideOverlay();
        api.showNoRowsOverlay();
        break;
      case LOADING:
        api.hideOverlay();
        api.showLoadingOverlay();
        break;
      case SUCCESS:
        if(isRowPresent()) {
          api.hideOverlay();
        } else {
          api.showNoRowsOverlay();
        }
      break;
    }
  }, []);

  useEffect(() => {
    showGridOverlay(showErrorMessage);
  }, [showErrorMessage, showGridOverlay]);

  // Cleanup effect to properly destroy grid and prevent memory leaks from detached DOM elements
  useEffect(() => {
    return () => {
      const api = gridApiRef.current;
      if (api) {
        try {
          if (typeof api.destroy === 'function') {
            api.destroy();
          }
          gridApiRef.current = null;
        } catch (error) {
          // Silently handle any errors during cleanup
          console.warn('Error during grid cleanup:', error);
        }
      }
    };
  }, []);

  const handleModelUpdateOverlay = useCallback(() => {
    const api = gridApiRef.current;
    if (isRowPresent()) {
      api.hideOverlay();
    } else if (showErrorMessage) {
      showGridOverlay(showErrorMessage);
    } else {
      api.showNoRowsOverlay();
    }
  }, [showErrorMessage, showGridOverlay, isRowPresent]);

  const onGridModelUpdated = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    handleModelUpdateOverlay();
    onModelUpdated?.(api);
    if (hasActiveColumnAndQuickFilter?.()) {
      onGridFilterChanged?.(api);
    }
  }, [
    onModelUpdated,
    handleModelUpdateOverlay,
    onGridFilterChanged,
    hasActiveColumnAndQuickFilter
  ]);

  const resizeGrid = useCallback(({ api }) => {
    gridApiRef.current = api;
    api.sizeColumnsToFit();
  }, []);

  const onGridAPIReady = useCallback(
    (gridApi) => {
      gridApi.api.gridName = config.title;
      resizeGrid(gridApi);

      onGridReady?.(gridApi);
      if (layoutContainer?.layoutManager?.eventHub) {
        layoutContainer.layoutManager.eventHub.emit(
          `gridHeader_${layoutContainer._config.id}_setGridApi`,
          gridApi.api
        );
      }
      if (showErrorMessage) {
        showGridOverlay(showErrorMessage);
      }
    },
    [config.title, resizeGrid, showErrorMessage, onGridReady, layoutContainer, showGridOverlay]
  );

  const customNavigation = useCallback((params) => {
    const KEY_UP = 'ArrowUp';
    const KEY_DOWN = 'ArrowDown';
    const KEY_LEFT = 'ArrowLeft';
    const KEY_RIGHT = 'ArrowRight';
    const { previousCellPosition, nextCellPosition, key } = params;
    if ([KEY_LEFT, KEY_RIGHT].includes(key)) {
      return nextCellPosition;
    }
    if ([KEY_UP, KEY_DOWN].includes(key)) {
      const targetRowIndex = previousCellPosition.rowIndex + (key === KEY_DOWN ? 1 : -1);
      gridApiRef.current.forEachNode((node) => {
        if (node.rowIndex === targetRowIndex) {
          node.setSelected(true, true);
          return true;
        }
      });
      return nextCellPosition;
    }
    return null;
  }, []);

  const noRowsOverlayComponentParams = useMemo(() => {
    const { NO_DATA, ERROR } = GRID_CONSTANTS.GRID_OVERLAY_STATUS;
    const message =
      showErrorMessage === appConstants.GRID_STATUS.ERROR ? ERROR.MESSAGE : NO_DATA.MESSAGE;
    return { message, error: showErrorMessage };
  }, [showErrorMessage]);

  const loadingOverlayComponentParams = useMemo(() => {
    return { loadingMessage: GRID_CONSTANTS.GRID_OVERLAY_STATUS.LOADING.MESSAGE };
  }, []);

  const onFirstDataRendered = useCallback(({ api }) => {
    api.sizeColumnsToFit();
  }, []);

  const handleOnRowDataUpdated = useCallback((params) => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    onRowDataUpdated?.(params);
  }, []);

  const processUnpinnedColumns = (_params) => {
    return []; // BUGID: 187207: please refer to the ticket for the details
  };

  return (
    <div className={`gridTheme-${theme.themeName}`}>
      <div className={`ag-theme-material theme-${theme.themeName}`}>
        <AgGridReact
          theme={themes[theme.themeName]}
          rowSelection="single"
          rowDeselection="true"
          suppressMovableColumns={false}
          suppressDragLeaveHidesColumns
          suppressCsvExport={false}
          stopEditingWhenGridLosesFocus
          animateRows
          loadingOverlayComponent={CustomLoadingOverlay}
          loadingOverlayComponentParams={loadingOverlayComponentParams}
          noRowsOverlayComponent={CustomNoRowsOverlay}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          suppressColumnVirtualisation={false}
          rowHeight={30}
          headerHeight={30}
          navigateToNextCell={customNavigation}
          components={{
            agColumnHeader: CustomGridHeader
          }}
          onFilterChanged={onGridFilterChanged}
          scrollbarWidth={17}
          onGridReady={onGridAPIReady}
          onGridSizeChanged={resizeGrid}
          onColumnMoved={onColumnMoved}
          onDragStopped={onDragStopped}
          suppressPropertyNamesCheck={true} // Note: Migrate custom properties to context object as suppressPropertyNamesCheck is deprecated in AG Grid v33+
          onModelUpdated={onGridModelUpdated}
          rowData={rowData}
          suppressHeaderFocus
          stopEditingWhenCellsLoseFocus
          onFirstDataRendered={onFirstDataRendered}
          onRowDataUpdated={handleOnRowDataUpdated}
          processUnpinnedColumns={processUnpinnedColumns}
          includeHiddenColumnsInQuickFilter={true}
          {...other}
        />
      </div>
      {panelLabel && (
        <PanelLabel
          panelLabel={panelLabel}
          filteredData={gridApiRef.current?.isColumnFilterPresent?.() ? filteredData : rowData}
        />
      )}
    </div>
  );
};

export default compose(withTheme(), withUserProfile)(DataGridNew);
